import { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, ArrowRight } from "lucide-react";
import { waLink } from "@/config/journeys";
import { trJourney } from "@/config/journeyI18n";
import { useLanguage } from "@/context/LanguageContext";
import { trackEvent } from "@/api/client";

// The unified WhatsApp-first conversion bridge. Optional name field personalizes
// the pre-filled GiriChat message; a secondary link lets decisive buyers purchase
// directly.
export const ConversionCTA = ({ module, giriWhatsapp, bookUrl, slug, pathLabel }) => {
  const { lang } = useLanguage();
  const [name, setName] = useState("");
  const tag = `Interested - ${pathLabel || "Krishna"}${slug ? ` · ${slug}` : ""}`;
  const href = waLink(giriWhatsapp, module.wa_message, name, tag);
  const book = module.book_enabled && bookUrl;

  const onChat = () => {
    if (slug) trackEvent(slug, "cta_click");
  };

  return (
    <div data-testid={`journey-conversion-${module.id}`} className="mx-auto max-w-xl px-5">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.6 }}
        className="rounded-3xl border border-amber-300/25 bg-gradient-to-b from-[#1a2038] to-[#0a0f24] p-8 text-center shadow-[0_0_60px_rgba(251,191,36,0.15)]"
      >
        <h3 className="font-display text-3xl leading-tight text-amber-200 sm:text-4xl">
          {trJourney(module.headline, lang)}
        </h3>
        {module.body && (
          <p className="mx-auto mt-3 max-w-md text-sm text-amber-50/60 md:text-base">{trJourney(module.body, lang)}</p>
        )}

        {module.name_field && (
          <input
            data-testid="conversion-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={lang === "hi" ? "आपका नाम (वैकल्पिक)" : "Your name (optional)"}
            className="mx-auto mt-6 block w-full max-w-sm rounded-full border border-amber-300/20 bg-[#080b16]/70 px-5 py-3 text-center text-sm text-amber-50 outline-none placeholder:text-amber-100/30 focus:border-amber-300/60"
          />
        )}

        <a
          data-testid="conversion-whatsapp-btn"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onChat}
          className="group mx-auto mt-5 inline-flex w-full max-w-sm items-center justify-center gap-2 rounded-full bg-amber-400 px-6 py-3.5 text-sm font-semibold text-[#0a0f24] transition-all hover:bg-amber-300"
        >
          <MessageCircle className="h-4 w-4" />
          {trJourney(module.wa_label, lang)}
        </a>

        {book ? (
          <a
            data-testid="conversion-book-link"
            href={bookUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-4 inline-flex items-center justify-center gap-1.5 text-sm text-amber-300/80 underline-offset-4 transition-colors hover:text-amber-200 hover:underline"
          >
            {trJourney(module.book_label || "Book Your Copy Directly", lang)}
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </a>
        ) : null}
      </motion.div>
    </div>
  );
};
